import { usePlayerActions } from "@utils/player/core/usePlayer";

import { auth } from "@utils/firebase";
import { useAuthSignOut } from "@utils/firebaseAuth/query/auth";
import {
  useApi,
  useData,
  useEndSession,
  usePlayerEngine,
} from "../runtime/provider";
import { useLandingStore } from "store/useLandingStore";
import { useUpdateUser } from "@hooks/trpc/useMe";
import { Platform } from "react-native";
import { router } from "expo-router";
import sdk from "@farcaster/miniapp-sdk";
import { clearStoredVendorAttempts } from "@components/Checkout/vendor/attemptStorage";
import { captureException } from "@utils/captureException";
import { createLogout } from "./logout";

export const useLogout = (componentName: string) => {
  const playerActions = usePlayerActions();
  const player = usePlayerEngine();
  const data = useData();

  const { mutateAsync: logUserOut } = useAuthSignOut(auth);
  const updateUser = useUpdateUser();
  const api = useApi();
  const endSession = useEndSession();

  return createLogout({
    clearPushTokens: () =>
      updateUser.mutateAsync({
        expoPushToken: null,
        fcmToken: null,
        apnToken: null,
      }),
    endPlayback: () => player.lifecycle.end("user"),
    pausePlayback: () => playerActions.pause(),
    resetPlayback: () => playerActions.reset(),
    flushPlays: data.reporting.flushPlaysBeforeLogout,
    // The client starts acquiring the current user's token synchronously.
    recordSignOut: () => api.identity.signOut.mutate(),
    signOut: () => logUserOut(),
    endSession: () => endSession("sign-out"),
    restart: () => useLandingStore.getState().restart(),
    report: (error, operation) =>
      captureException(error, {
        channel: "error",
        area: "identity",
        operation,
        tags: { componentName },
      }),
    afterLogout: async () => {
      if (Platform.OS === "web") {
        try {
          clearStoredVendorAttempts();
        } catch {
          // Private browsing can disable storage; logout still proceeds.
        }
        // Check if we're in a Farcaster Mini App
        try {
          const isInMiniApp = await sdk.isInMiniApp();
          if (isInMiniApp) {
            // In mini app: use window.location directly (similar to root index route)
            // Navigate to home feed (the main feed route)
            window.location.replace("/home");
          } else {
            // Normal browser: use router navigation
            router.navigate("../");
            router.replace("/?reload=true");
          }
        } catch (error) {
          // If SDK check fails, fall back to normal router navigation
          console.warn(
            "Failed to check mini app status, using normal navigation:",
            error,
          );
          router.navigate("../");
          router.replace("/?reload=true");
        }
      }
    },
  });
};
